import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Plus, CheckCircle, FileText, TrendingDown } from "lucide-react";

// Данные для графика динамики долгов
const debtData = [
  { month: "Янв", amount: 150000000 },
  { month: "Фев", amount: 145000000 },
  { month: "Мар", amount: 130000000 },
  { month: "Апр", amount: 125000000 },
  { month: "Май", amount: 110000000 },
  { month: "Июн", amount: 95000000 },
  { month: "Июл", amount: 80000000 },
];

// Список завершённых зачётов
const completedOffsets = [
  {
    id: 1,
    counterparty: 'ТОО "Партнёр А"',
    amount: 45000000,
    date: "15.07.2025",
    type: "Взаимозачёт",
  },
  {
    id: 2,
    counterparty: "ИП Иванов С.П.",
    amount: 28000000,
    date: "10.06.2025",
    type: "Взаимозачёт",
  },
  {
    id: 3,
    counterparty: 'ТОО "Компания Б"',
    amount: 62000000,
    date: "25.05.2025",
    type: "Трёхсторонний",
  },
  {
    id: 4,
    counterparty: 'ТОО "Торговый Дом"',
    amount: 35000000,
    date: "18.04.2025",
    type: "Взаимозачёт",
  },
];

export const Dashboard = () => {
  const totalDebt = 80000000;
  const debtReduction = 70000000;
  const completedCount = completedOffsets.length;

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(to bottom right, #f8fafc, #f1f5f9)",
      padding: "24px",
      marginTop: "10px",
      borderRadius: "10px",
    },
    maxWidth: {
      maxWidth: "1280px",
      margin: "0 auto",
    },
    header: {
      marginBottom: "32px",
    },
    title: {
      fontSize: "30px",
      fontWeight: "bold",
      color: "#1e293b",
      marginBottom: "8px",
    },
    subtitle: {
      color: "#475569",
    },
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "24px",
      marginBottom: "32px",
    },
    statCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      padding: "24px",
      border: "1px solid #e2e8f0",
    },
    statHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "8px",
    },
    statLabel: {
      color: "#475569",
      fontSize: "14px",
      fontWeight: "500",
    },
    statValue: {
      fontSize: "30px",
      fontWeight: "bold",
      color: "#1e293b",
    },
    statValueGreen: {
      fontSize: "30px",
      fontWeight: "bold",
      color: "#16a34a",
    },
    chartCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      padding: "24px",
      marginBottom: "32px",
      border: "1px solid #e2e8f0",
    },
    chartTitle: {
      fontSize: "20px",
      fontWeight: "bold",
      color: "#1e293b",
      marginBottom: "24px",
    },
    tableCard: {
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      border: "1px solid #e2e8f0",
    },
    tableHeader: {
      padding: "24px",
      borderBottom: "1px solid #e2e8f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    tableTitle: {
      fontSize: "20px",
      fontWeight: "bold",
      color: "#1e293b",
    },
    createButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      backgroundColor: "#0078FF",
      color: "white",
      padding: "10px 16px",
      borderRadius: "8px",
      fontWeight: "500",
      border: "none",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    thead: {
      backgroundColor: "#f8fafc",
    },
    th: {
      textAlign: "left",
      padding: "16px 24px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#334155",
    },
    thRight: {
      textAlign: "right",
      padding: "16px 24px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#334155",
    },
    thCenter: {
      textAlign: "center",
      padding: "16px 24px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#334155",
    },
    tr: {
      borderBottom: "1px solid #e2e8f0",
      transition: "background-color 0.2s",
    },
    td: {
      padding: "16px 24px",
    },
    tdRight: {
      padding: "16px 24px",
      textAlign: "right",
    },
    counterparty: {
      fontWeight: "500",
      color: "#1e293b",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 12px",
      borderRadius: "9999px",
      fontSize: "14px",
      backgroundColor: "#dbeafe",
      color: "#1e40af",
    },
    amount: {
      fontWeight: "600",
      color: "#1e293b",
    },
    date: {
      color: "#475569",
    },
    statusCell: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    statusText: {
      fontSize: "14px",
      fontWeight: "500",
      color: "#16a34a",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.maxWidth}>
        {/* Заголовок */}
        <div style={styles.header}>
          <h1 style={styles.title}>Дашборд клиента</h1>
          <p style={styles.subtitle}>
            Управление взаимозачётами и мониторинг задолженности
          </p>
        </div>

        {/* Статистические карточки */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statHeader}>
              <span style={styles.statLabel}>Текущий долг</span>
              <FileText
                style={{ width: "20px", height: "20px", color: "#3b82f6" }}
              />
            </div>
            <div style={styles.statValue}>
              {totalDebt.toLocaleString("ru-RU")} ₸
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statHeader}>
              <span style={styles.statLabel}>Погашено за период</span>
              <TrendingDown
                style={{ width: "20px", height: "20px", color: "#16a34a" }}
              />
            </div>
            <div style={styles.statValueGreen}>
              -{debtReduction.toLocaleString("ru-RU")} ₸
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statHeader}>
              <span style={styles.statLabel}>Завершено зачётов</span>
              <CheckCircle
                style={{ width: "20px", height: "20px", color: "#a855f7" }}
              />
            </div>
            <div style={styles.statValue}>{completedCount}</div>
          </div>
        </div>

        {/* График динамики долгов */}
        <div style={styles.chartCard}>
          <h2 style={styles.chartTitle}>Динамика погашения задолженности</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={debtData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="month"
                stroke="#64748b"
                style={{ fontSize: "14px" }}
              />
              <YAxis
                stroke="#64748b"
                style={{ fontSize: "14px" }}
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}к`}
              />
              <Tooltip
                formatter={(value) => [
                  `${value.toLocaleString("ru-RU")} ₸`,
                  "Долг",
                ]}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
              />
              <Line
                type="monotone"
                dataKey="amount"
                stroke="#3b82f6"
                strokeWidth={3}
                dot={{ fill: "#3b82f6", r: 5 }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Список завершённых зачётов */}
        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <h2 style={styles.tableTitle}>Завершённые зачёты</h2>
            <button style={styles.createButton}>
              <Plus style={{ width: "20px", height: "20px" }} />
              Создать новый
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  <th style={styles.th}>Контрагент</th>
                  <th style={styles.th}>Тип зачёта</th>
                  <th style={styles.thRight}>Сумма</th>
                  <th style={styles.thRight}>Дата</th>
                  <th style={styles.thCenter}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {completedOffsets.map((offset) => (
                  <tr
                    key={offset.id}
                    style={styles.tr}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8fafc")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <td style={styles.td}>
                      <div style={styles.counterparty}>
                        {offset.counterparty}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge}>{offset.type}</span>
                    </td>
                    <td style={styles.tdRight}>
                      <span style={styles.amount}>
                        {offset.amount.toLocaleString("ru-RU")} ₸
                      </span>
                    </td>
                    <td style={styles.tdRight}>
                      <span style={styles.date}>{offset.date}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.statusCell}>
                        <CheckCircle
                          style={{
                            width: "20px",
                            height: "20px",
                            color: "#16a34a",
                          }}
                        />
                        <span style={styles.statusText}>Завершён</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
